import { createRouter, createWebHistory } from "vue-router";
import {
  canAccessRoute,
  getDashboardRoute,
  getLoginRoute,
  getLoginRouteForPath,
  isPanelistRole,
  isPathAllowedForPanelist,
  roleOf,
  safeRedirect,
} from "@/helpers/routeAccess.mjs";

const routes = [
  {
    path: "/",
    redirect: "/seeker/login",
  },
  {
    path: "/login",
    redirect: (to) => ({ path: getLoginRoute(getUserRole()), query: to.query }),
  },
  // Seeker Auth
  {
    path: "/seeker/login",
    name: "SeekerLoginPassword",
    component: () => import("@/views/auth/SeekerLoginPassword.vue"),
    meta: { guest: true },
  },
  {
    path: "/seeker/login-otp",
    name: "SeekerLoginOtp",
    component: () => import("@/views/auth/SeekerLoginOtp.vue"),
    meta: { guest: true },
  },
  {
    path: "/seeker/register",
    name: "SeekerRegister",
    component: () => import("@/views/auth/SeekerRegister.vue"),
    meta: { guest: true },
  },
  // Employer Auth
  {
    path: "/employer/login",
    name: "EmployerLoginPassword",
    component: () => import("@/views/auth/EmployerLoginPassword.vue"),
    meta: { guest: true },
  },
  {
    path: "/employer/login-otp",
    name: "EmployerLoginOtp",
    component: () => import("@/views/auth/EmployerLoginOtp.vue"),
    meta: { guest: true },
  },
  {
    path: "/employer/register",
    name: "EmployerRegister",
    component: () => import("@/views/auth/EmployerRegister.vue"),
    meta: { guest: true },
  },
  // Forgot Password
  {
    path: "/forgot-password",
    name: "ForgotPassword",
    component: () => import("@/views/auth/ForgotPassword.vue"),
    meta: { guest: true },
  },
  {
    path: "/employer/dashboard",
    name: "EmployerDashboard",
    component: () => import("@/views/employer/Dashboard.vue"),
    meta: { requiresAuth: true, role: "Employer" },
  },
  {
    path: "/seeker/dashboard",
    name: "SeekerDashboard",
    component: () => import("@/views/seeker/Dashboard.vue"),
    meta: { requiresAuth: true, role: "Seeker" },
  },
  {
    path: "/employer/profile",
    name: "EmployerProfile",
    component: () => import("@/views/employer/Profile.vue"),
    meta: { requiresAuth: true, role: "Employer" },
  },
  {
    path: "/employer/gallery",
    name: "EmployerGallery",
    component: () => import("@/views/employer/Gallery.vue"),
    meta: { requiresAuth: true, role: "Employer" },
  },
  {
    path: "/seeker/profile",
    name: "SeekerProfile",
    component: () => import("@/views/seeker/Profile.vue"),
    meta: { requiresAuth: true, role: "Seeker" },
  },
  {
    path: "/seeker/resume",
    name: "SeekerResume",
    component: () => import("@/views/seeker/Resume.vue"),
    meta: { requiresAuth: true, role: "Seeker" },
  },
  // Employer — Phase 3
  {
    path: "/employer/post-job",
    name: "EmployerPostJob",
    component: () => import("@/views/employer/PostJob.vue"),
    meta: { requiresAuth: true, role: "Employer" },
  },
  {
    path: "/employer/jobs",
    name: "EmployerManageJobs",
    component: () => import("@/views/employer/ManageJobs.vue"),
    meta: { requiresAuth: true, role: "Employer" },
  },
  {
    path: "/employer/applications",
    name: "EmployerApplications",
    component: () => import("@/views/employer/Applications.vue"),
    meta: { requiresAuth: true, role: "Employer" },
  },
  {
    path: "/employer/applications/:applicationId/interviews",
    name: "EmployerScheduleInterview",
    component: () => import("@/views/employer/ScheduleInterview.vue"),
    meta: { requiresAuth: true, role: "Employer" },
  },
  {
    path: "/employer/selected",
    name: "EmployerSelectedCandidates",
    component: () => import("@/views/employer/CandidateSelections.vue"),
    meta: { requiresAuth: true, role: "Employer" },
  },
  {
    path: "/employer/wallet",
    name: "EmployerWallet",
    component: () => import("@/views/employer/Wallet.vue"),
    meta: { requiresAuth: true, role: "Employer" },
  },
  {
    path: "/employer/interviews",
    name: "EmployerInterviews",
    component: () => import("@/views/employer/Interviews.vue"),
    meta: { requiresAuth: true, role: "Employer" },
  },
  {
    path: "/employer/interviews/:interviewId/feedback",
    name: "EmployerInterviewFeedback",
    component: () => import("@/views/employer/InterviewFeedback.vue"),
    meta: { requiresAuth: true, role: "Employer" },
  },
  // Seeker — Phase 3
  {
    path: "/seeker/search-jobs",
    name: "SeekerSearchJobs",
    component: () => import("@/views/seeker/SearchJobs.vue"),
    meta: { requiresAuth: true, role: "Seeker" },
  },
  {
    path: "/seeker/applied-jobs",
    name: "SeekerAppliedJobs",
    component: () => import("@/views/seeker/AppliedJobs.vue"),
    meta: { requiresAuth: true, role: "Seeker" },
  },
  // Seeker — Phase 4
  {
    path: "/seeker/interviews",
    name: "SeekerInterviews",
    component: () => import("@/views/seeker/Interviews.vue"),
    meta: { requiresAuth: true, role: "Seeker" },
  },
  {
    path: "/seeker/shortlisted",
    name: "SeekerShortlistedJobs",
    component: () => import("@/views/seeker/ShortlistedJobs.vue"),
    meta: { requiresAuth: true, role: "Seeker" },
  },
  // Seeker — Phase 5
  {
    path: "/seeker/plans",
    name: "SeekerPlans",
    component: () => import("@/views/seeker/Plans.vue"),
    meta: { requiresAuth: true, role: "Seeker" },
  },
  // Employer — Placeholder routes (VPSC menu items)
  {
    path: "/employer/view-profile",
    name: "EmployerViewProfile",
    // Panelists share the /employer/* URL tree but have no employer profile;
    // hitting the employer ViewProfile screen returns blank because there's
    // no JcEmployer row. Route to the panelist-specific view when the caller
    // is a JcPanelist so the same sidebar entry lands both roles on the
    // right screen.
    component: () => {
      const role = (JSON.parse(localStorage.getItem("userdata") || "{}").role || "").toLowerCase();
      if (role === "panelist" || role === "jcpanelist") {
        return import("@/views/panelist/PanelistProfile.vue");
      }
      return import("@/views/employer/ViewProfile.vue");
    },
    meta: { requiresAuth: true, title: "View Profile" },
  },
  {
    path: "/panelist/earnings",
    name: "PanelistEarnings",
    component: () => import("@/views/panelist/PanelistEarnings.vue"),
    meta: { requiresAuth: true, title: "My Earnings" },
  },
  {
    path: "/panelist/expert-verified",
    name: "PanelistExpertVerifiedInbox",
    component: () => import("@/views/panelist/ExpertVerifiedInbox.vue"),
    meta: { requiresAuth: true, title: "Expert Verified Requests" },
  },
  {
    path: "/employer/share-experience",
    name: "EmployerShareExperience",
    component: () => import("@/views/shared/ShareExperience.vue"),
    meta: { requiresAuth: true, role: "Employer", title: "Share Your Experience" },
  },
  {
    path: "/employer/change-password",
    name: "EmployerChangePassword",
    component: () => import("@/views/shared/ChangePassword.vue"),
    meta: { requiresAuth: true, role: "Employer", title: "Change Password" },
  },
  {
    path: "/employer/delete-account",
    name: "EmployerDeleteAccount",
    component: () => import("@/views/shared/DeleteAccount.vue"),
    meta: { requiresAuth: true, role: "Employer", title: "Delete Account" },
  },
  // Seeker — Placeholder routes (VPSC menu items)
  {
    path: "/seeker/build-resume",
    name: "SeekerBuildResume",
    component: () => import("@/views/seeker/BuildResume.vue"),
    meta: { requiresAuth: true, role: "Seeker", title: "Build Your Resume" },
  },
  {
    path: "/seeker/saved-jobs",
    name: "SeekerSavedJobs",
    component: () => import("@/views/seeker/SavedJobs.vue"),
    meta: { requiresAuth: true, role: "Seeker", title: "Saved Jobs" },
  },
  {
    path: "/seeker/view-profile",
    name: "SeekerViewProfile",
    component: () => import("@/views/seeker/ViewProfile.vue"),
    meta: { requiresAuth: true, role: "Seeker", title: "View Profile" },
  },
  {
    path: "/seeker/change-password",
    name: "SeekerChangePassword",
    component: () => import("@/views/shared/ChangePassword.vue"),
    meta: { requiresAuth: true, role: "Seeker", title: "Change Password" },
  },
  {
    path: "/seeker/share-experience",
    name: "SeekerShareExperience",
    component: () => import("@/views/shared/ShareExperience.vue"),
    meta: { requiresAuth: true, role: "Seeker", title: "Share Your Experience" },
  },
  {
    path: "/seeker/profile-highlights",
    name: "SeekerProfileHighlights",
    component: () => import("@/views/seeker/ProfileHighlights.vue"),
    meta: { requiresAuth: true, role: "Seeker", title: "Profile Highlights" },
  },
  {
    path: "/seeker/expert-verified",
    name: "SeekerExpertVerified",
    component: () => import("@/views/seeker/ExpertVerified.vue"),
    meta: { requiresAuth: true, role: "Seeker", title: "Sunhera Expert Verified" },
  },
  {
    path: "/seeker/expert-verified/interview",
    name: "seeker-expert-verified-interview",
    component: () => import("@/views/seeker/ExpertVerifiedInterview.vue"),
    meta: { requiresAuth: true, role: "Seeker", title: "Expert Verified Interview" },
  },
  {
    path: "/seeker/feedback-verified",
    name: "SeekerFeedbackVerified",
    component: () => import("@/views/seeker/FeedbackVerified.vue"),
    meta: { requiresAuth: true, role: "Seeker", title: "Feedback Verified" },
  },
  {
    path: "/seeker/alerts",
    name: "SeekerJobAlerts",
    component: () => import("@/views/seeker/JobAlerts.vue"),
    meta: { requiresAuth: true, role: "Seeker", title: "Job Alerts" },
  },
  {
    path: "/seeker/alerts/unsubscribe",
    name: "SeekerJobAlertsUnsubscribe",
    component: () => import("@/views/public/UnsubscribeJobAlerts.vue"),
    // Public, not guest: the link arrives by email and must work whether or not the
    // reader is signed in, so the guard lets it through before any guest/auth redirect.
    meta: { public: true, title: "Unsubscribe" },
  },
  {
    path: "/seeker/delete-account",
    name: "SeekerDeleteAccount",
    component: () => import("@/views/shared/DeleteAccount.vue"),
    meta: { requiresAuth: true, role: "Seeker", title: "Delete Account" },
  },
  // Admin impersonation entry point
  {
    path: "/admin-impersonate",
    name: "AdminImpersonate",
    component: { template: "<div>Redirecting...</div>" },
    meta: { guest: true },
  },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
  scrollBehavior() {
    return { top: 0 };
  },
});

/**
 * Navigation guard: redirect unauthenticated users to login,
 * and after login redirect to role-appropriate dashboard.
 */
router.beforeEach((to, from, next) => {
  // Handle admin impersonation via hash fragment
  if (to.path === "/admin-impersonate") {
    const hash = window.location.hash || "";
    if (hash.includes("data=")) {
      try {
        const encoded = hash.split("data=")[1];
        const authData = JSON.parse(decodeURIComponent(encoded));

        // Clear previous session and set new one
        localStorage.removeItem("jwt");
        localStorage.removeItem("refreshToken");
        localStorage.removeItem("user");
        localStorage.removeItem("userdata");
        localStorage.removeItem("companyId");

        localStorage.setItem("jwt", authData.token);
        if (authData.refreshToken) localStorage.setItem("refreshToken", authData.refreshToken);
        localStorage.setItem("user", JSON.stringify(authData.user));
        localStorage.setItem("userdata", JSON.stringify(authData.user));
        if (authData.companyId) localStorage.setItem("companyId", String(authData.companyId));

        const role = authData.user.role || "";
        const dashboard = getDashboardRoute(role);

        // Optional ?redirect=<path> from the admin impersonate link — must be a
        // same-origin path under /employer/ (never off-site, never absolute).
        // Falls back to the role-default dashboard when missing or invalid.
        let target = dashboard;
        const rawRedirect = to.query?.redirect;
        if (rawRedirect && typeof rawRedirect === "string") {
          try {
            const decoded = decodeURIComponent(rawRedirect);
            if (decoded.startsWith("/employer/") && !decoded.startsWith("//")) {
              target = decoded;
            }
          } catch { /* ignore malformed redirect */ }
        }

        // Clean URL and do a full page reload to reinitialize the app with new auth
        history.replaceState(null, "", target);
        window.location.reload();
        return;
      } catch (e) {
        console.error("Failed to process impersonation:", e);
        return next("/login");
      }
    }
  }

  // A ?redirect= is followed by the login screens after sign-in; drop any that could leave
  // the app (absolute URL, protocol-relative "//host", backslash tricks).
  if (to.query.redirect != null && !safeRedirect(to.query.redirect)) {
    const query = { ...to.query };
    delete query.redirect;
    return next({ path: to.path, query, hash: to.hash, replace: true });
  }

  // Public pages (e.g. the emailed job-alert unsubscribe link) open for everyone.
  if (to.meta.public) return next();

  const token = localStorage.getItem("jwt");
  const isAuthenticated = !!token && !isTokenExpired(token);

  // Redirect authenticated users away from guest-only pages
  if (to.meta.guest && isAuthenticated) {
    const role = getUserRole();
    const dashboard = getDashboardRoute(role);
    if (dashboard !== "/login") return next(dashboard);
  }

  // Redirect unauthenticated users to the login for the page's side (clear stale tokens)
  if (to.meta.requiresAuth && !isAuthenticated) {
    if (token) {
      // Token exists but expired — clear it
      localStorage.removeItem("jwt");
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("user");
      localStorage.removeItem("userdata");
      localStorage.removeItem("companyId");
    }
    return next({ path: getLoginRouteForPath(to.path), query: { redirect: to.fullPath } });
  }

  // Role scope: a Seeker route needs a seeker, an Employer route an employer or panelist.
  // Panelists share the /employer/* URL tree but are held to a whitelist (post-job,
  // manage-jobs, wallet, applications etc. stay employer-only). Anyone else goes to
  // their own dashboard.
  if (to.meta.requiresAuth && isAuthenticated) {
    const role = getUserRole();
    if (!canAccessRoute(role, to.meta.role, to.path)) {
      return next(getDashboardRoute(role));
    }
  }

  next();
});

/**
 * Check if a JWT token is expired by decoding the payload.
 */
function isTokenExpired(token) {
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload.exp * 1000 < Date.now();
  } catch {
    return true;
  }
}

/**
 * Extract user role from localStorage userdata.
 * @returns {string} e.g. "JcEmployer", "JcSeeker", "JcPanelist", or empty string
 */
function getUserRole() {
  try {
    return roleOf(JSON.parse(localStorage.getItem("userdata") || "{}"));
  } catch {
    return "";
  }
}

export { getUserRole, getDashboardRoute, getLoginRoute, isPanelistRole, isPathAllowedForPanelist };
export default router;
